import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Pencil, Plus, Trash2, X } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { eur } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const EMPTY = { name: "", ranges: [{ from: "", to: "" }], priority: 0, min_nights: 1, active: true, prices: {} };
const inputCls = "mt-1 bg-[#F7F5F0] border-[#DCD8D0]";
const fmt = (s) => (s ? s.split("-").reverse().join("/") : "");

export default function Seasons() {
  const [seasons, setSeasons] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, v] = await Promise.all([api.get("/fleet/seasons"), api.get("/fleet/vehicles")]);
      setSeasons(s.data.seasons || []);
      setVehicles(v.data.vehicles || []);
    } catch (err) {
      toast.error(formatError(err));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setRange = (i, k) => (e) =>
    setEditing((d) => ({ ...d, ranges: d.ranges.map((r, j) => (j === i ? { ...r, [k]: e.target.value } : r)) }));
  const setPrice = (vid) => (e) =>
    setEditing((d) => ({ ...d, prices: { ...d.prices, [vid]: e.target.value } }));

  const save = async () => {
    const prices = Object.fromEntries(
      Object.entries(editing.prices || {})
        .filter(([, p]) => p !== "" && p !== null && p !== undefined)
        .map(([k, p]) => [k, Number(p)]),
    );
    const payload = {
      name: editing.name, ranges: editing.ranges, priority: Number(editing.priority),
      min_nights: Number(editing.min_nights), active: !!editing.active, prices,
    };
    setSaving(true);
    try {
      if (editing.id) await api.put(`/fleet/seasons/${editing.id}`, payload);
      else await api.post("/fleet/seasons", payload);
      toast.success("Temporada guardada");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    try {
      await api.delete(`/fleet/seasons/${s.id}`);
      load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setConfirmId(null);
    }
  };

  const vehicleName = (id) => vehicles.find((v) => v.id === id)?.name || "Vehículo borrado";

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <header className="bg-white border-b border-[#DCD8D0]">
        <div className="container-x py-4 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center text-[#5C5C5C] hover:text-marca">
            <ArrowLeft className="h-4 w-4 mr-2" /> Panel
          </Link>
          <Button onClick={() => setEditing({ ...EMPTY, ranges: [{ from: "", to: "" }], prices: {} })} className="bg-acento hover:bg-acento-hover text-white" data-testid="seasons-new-btn">
            <Plus className="h-4 w-4 mr-2" /> Nueva temporada
          </Button>
        </div>
      </header>

      <main className="container-x py-8">
        <span className="text-overline">Precios</span>
        <h1 className="font-display text-3xl font-bold tracking-tight text-[#1A1A1A] mt-2 mb-2">Temporadas</h1>
        <p className="text-sm text-[#5C5C5C] mb-6">
          Fuera de temporada cada vehículo cobra su precio base. Si dos temporadas coinciden, manda la de mayor prioridad.
          El mínimo de noches de una temporada vale para toda la flota.
        </p>
        <div className="space-y-3">
          {seasons.length === 0 && (
            <div className="bg-white border border-[#DCD8D0] rounded-xl p-8 text-center text-[#8A8A8A]" data-testid="seasons-empty">
              Sin temporadas: todas las noches se cobran al precio base de cada vehículo.
            </div>
          )}
          {seasons.map((s) => (
            <div key={s.id} className="bg-white border border-[#DCD8D0] rounded-xl p-4 flex items-start gap-4" data-testid={`season-row-${s.name}`}>
              <div className="flex-1">
                <div className="font-medium text-[#1A1A1A]">
                  {s.name} {!s.active && <span className="text-xs text-[#8A8A8A]">(inactiva)</span>}
                </div>
                <div className="text-xs text-[#8A8A8A]">
                  {s.ranges.map((r) => `${fmt(r.from)} – ${fmt(r.to)}`).join(" · ")} · prioridad {s.priority} · mínimo {s.min_nights} {s.min_nights === 1 ? "noche" : "noches"}
                </div>
                <div className="text-sm text-[#5C5C5C] mt-1">
                  {Object.entries(s.prices || {}).map(([vid, p]) => `${vehicleName(vid)}: ${eur(p)}`).join(" · ") || "Sin precios propios (precio base)"}
                </div>
              </div>
              <Button variant="ghost" onClick={() => setEditing({ ...s, prices: { ...s.prices } })}><Pencil className="h-4 w-4" /></Button>
              {confirmId === s.id ? (
                <Button variant="destructive" onClick={() => remove(s)}>¿Borrar?</Button>
              ) : (
                <Button variant="ghost" onClick={() => setConfirmId(s.id)}><Trash2 className="h-4 w-4" /></Button>
              )}
            </div>
          ))}
        </div>
      </main>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? "Editar temporada" : "Nueva temporada"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-5" data-testid="season-form">
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="sm:col-span-3"><Label>Nombre</Label>
                  <Input className={inputCls} value={editing.name} onChange={(e) => setEditing((d) => ({ ...d, name: e.target.value }))} data-testid="season-name" />
                </div>
                <div><Label>Prioridad</Label>
                  <Input type="number" className={inputCls} value={editing.priority} onChange={(e) => setEditing((d) => ({ ...d, priority: e.target.value }))} />
                </div>
                <div><Label>Mínimo de noches</Label>
                  <Input type="number" className={inputCls} value={editing.min_nights} onChange={(e) => setEditing((d) => ({ ...d, min_nights: e.target.value }))} />
                </div>
                <label className="flex items-end gap-2 text-sm pb-2">
                  <Checkbox checked={!!editing.active} onCheckedChange={(v) => setEditing((d) => ({ ...d, active: Boolean(v) }))} /> Activa
                </label>
              </div>

              <div>
                <Label>Fechas (ambos días incluidos)</Label>
                {editing.ranges.map((r, i) => (
                  <div key={i} className="flex items-center gap-2 mt-2">
                    <Input type="date" className="bg-[#F7F5F0] border-[#DCD8D0]" value={r.from} onChange={setRange(i, "from")} data-testid={`season-from-${i}`} />
                    <span className="text-[#8A8A8A]">→</span>
                    <Input type="date" className="bg-[#F7F5F0] border-[#DCD8D0]" value={r.to} onChange={setRange(i, "to")} data-testid={`season-to-${i}`} />
                    {editing.ranges.length > 1 && (
                      <Button variant="ghost" onClick={() => setEditing((d) => ({ ...d, ranges: d.ranges.filter((_, j) => j !== i) }))}><X className="h-4 w-4" /></Button>
                    )}
                  </div>
                ))}
                <Button variant="outline" className="mt-2" onClick={() => setEditing((d) => ({ ...d, ranges: [...d.ranges, { from: "", to: "" }] }))}>
                  <Plus className="h-4 w-4 mr-1" /> Añadir fechas
                </Button>
              </div>

              <div>
                <Label>Precio por noche de cada vehículo (vacío = su precio base)</Label>
                <div className="grid sm:grid-cols-2 gap-3 mt-2">
                  {vehicles.map((v) => (
                    <div key={v.id}>
                      <div className="text-xs text-[#5C5C5C]">{v.name} · base {eur(v.base_price_per_night)}</div>
                      <Input type="number" step="0.01" className={inputCls} value={editing.prices?.[v.id] ?? ""} onChange={setPrice(v.id)} data-testid={`season-price-${v.slug}`} />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
                <Button onClick={save} disabled={saving} className="bg-marca hover:bg-marca-hover text-white" data-testid="season-save">
                  {saving ? "Guardando…" : "Guardar"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
